import { amorphicStatic, Persistable, remote, Remoteable, Supertype, supertypeClass } from '@haventech/amorphic';
import { Utils } from '../../../common/js/Utils';
import { IllustrationRequest } from '../../classes/wholelife/IllustrationRequest';
import { FetchResult } from 'apollo-fetch';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { ProductClient } from '@hit/product-client';
import { ProductApiTypes } from '@hit/policy-admin-types';
import { CoverpathProductClientCtx } from '../../../common/js/productService/CoverpathProductClientCtx';
import {
  PayloadSegmentInfo,
  PremiumSolveRequest,
  ProductServiceClient,
} from '../../../common/js/productService/ProductServiceClient';
import { remove } from 'lodash';
import { Product, RiderType } from '../../../common/js/static/Product';
import { serverOnly } from '../../../common/js/helpers';
import { Exchange1035FundingPurpose } from '../interfaces/CoverpathTypes';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class ProductClientController extends Remoteable(Persistable(Supertype)) {
  static new() {
    return new ProductClientController().init();
  }

  init() {
    return this;
  }

  @serverOnly()
  private async initProductClient(req: IllustrationRequest): Promise<ProductClient> {
    let UUID;

    if (!!req.requestId) {
      UUID = req.requestId;
    } else {
      UUID = Utils.generateUuid();
      req.requestId = UUID;
    }

    const msgCtx = {
      requestId: UUID,
      userId: UUID,
      referenceId: UUID,
      referenceType: 'Coverpath Product Client Initialization',
    };

    const productClientCtx = CoverpathProductClientCtx.new();
    const clientCtx = productClientCtx.newExecutionCtx(msgCtx);
    productClientCtx.exnCtx = clientCtx;
    productClientCtx.config.AUTH_TOKEN = amorphicStatic.config.nconf.get('cpApiGatewayJWTToken');
    return ProductClient.new(productClientCtx);
  }

  @remote({ on: 'server' })
  private async getVSTPayloadData(req: IllustrationRequest): Promise<void | FetchResult> {
    const productClient = await this.initProductClient(req);
    const requestPayload: ProductApiTypes.IllustrationPayloadReq = this.getProductServiceRequestPayload(req);
    const query = this.getVSTQueryParameters();
    try {
      const vstData = await productClient.getIllustration(requestPayload, query);
      LoggerSingleton.getInstance().info(
        {
          module: 'ProductClientController',
          function: 'getVSTPayloadData',
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
          data: {
            request: ProductClientController.cleanIllustrationRequestForLogging(req),
            query: JSON.stringify(query),
            responseSize: JSON.stringify(vstData).length,
          },
          category: Category.request,
        },
        'Response received from Product Client for VST Data'
      );
      return vstData;
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        stack: err.stack,
        message: err.message ? err.message : 'Response received from Product Client for VST Data with error',
      };
      LoggerSingleton.getInstance().error({
        module: 'ProductClientController',
        function: 'getVSTPayloadData',
        category: Category.request,
        error: error,
      });
    }
  }

  //todo:HL-36257 - clean up/combine illustration calls after concept illustration
  @remote({ on: 'server' })
  private async getConceptIllustrationVstData(req: IllustrationRequest, isSolve: boolean): Promise<void | FetchResult> {
    const productClient = await this.initProductClient(req);
    const requestPayload: ProductApiTypes.IllustrationPayloadReq =
      this.getConceptIllustrationProductServiceRequestPayload(req, isSolve);
    const query = this.getIllustrationTabVSTQueryParameters(isSolve);
    try {
      const vstData = await productClient.getIllustration(requestPayload, query);
      LoggerSingleton.getInstance().info(
        {
          module: 'ProductClientController',
          function: 'getConceptIllustrationVstData',
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
          data: {
            request: ProductClientController.cleanIllustrationRequestForLogging(req),
            query: JSON.stringify(query),
            responseSize: JSON.stringify(vstData).length,
          },
          category: Category.request,
        },
        'VST response received for Concept Illustration'
      );
      return vstData;
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        stack: err.stack,
        message: err.message
          ? err.message
          : 'Response received from Product Client for Concept Illustration VST Data with error',
      };
      LoggerSingleton.getInstance().error({
        module: 'ProductClientController',
        function: 'getConceptIllustrationVstData',
        category: Category.request,
        error: error,
      });

      return { data: null, errors: error.message };
    }
  }
  // Client Wrapper function for server side call.
  public async getVSTData(
    req: IllustrationRequest,
    illustrationTab?: boolean,
    isSolve?: boolean
  ): Promise<void | FetchResult> {
    return illustrationTab ? this.getConceptIllustrationVstData(req, isSolve) : this.getVSTPayloadData(req);
  }

  @remote({ on: 'server' })
  public async getPremiumSolve(req, desiredPremium: number, version: string): Promise<void | FetchResult> {
    const productClient = await this.initProductClient(req);
    const requestPayload: ProductApiTypes.PremiumSolvePayloadReq = this.getPremiumSolvePayload(
      req,
      desiredPremium,
      version
    );
    const query = this.getPremiumSolveQueryParameters();
    try {
      const premiumSolve = await productClient.getPremiumSolve(requestPayload, query);
      LoggerSingleton.getInstance().info(
        {
          module: 'ProductClientController',
          function: 'getPremiumSolve',
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
          data: {
            request: ProductClientController.cleanIllustrationRequestForLogging(req),
            query: JSON.stringify(query),
            responseSize: JSON.stringify(premiumSolve).length,
          },
          category: Category.request,
        },
        'Response received from Product Client for VST Data'
      );
      return premiumSolve;
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        stack: err.stack,
        message: err.message ? err.message : 'Response received from Product Client for VST Data with error',
      };
      LoggerSingleton.getInstance().error({
        module: 'ProductClientController',
        function: 'getPremiumSolve',
        category: Category.request,
        error: error,
      });
    }
  }

  /**
   * When using an IllustrationRequest for logging, we want to make sure there are no circular references or PII.
   */
  static cleanIllustrationRequestForLogging(original: IllustrationRequest): IllustrationRequest {
    const copy = { ...original };
    copy.customerPerson = null;
    copy.agent = null;

    return copy;
  }

  // ================= Request Payloads =================
  @serverOnly()
  private getProductServiceRequestPayload(req: IllustrationRequest): ProductApiTypes.IllustrationPayloadReq {
    let premiumType = null;
    if (req.cashValueTransfer) {
      if (req.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        premiumType = ProductApiTypes.TenThirtyFivePremiumType.PremiumAndALIR;
      } else if (req.applyTowards === Exchange1035FundingPurpose.ALIR_ONLY) {
        premiumType = ProductApiTypes.TenThirtyFivePremiumType.ALIR;
      }
    }
    const policy = {
      version: null,
      dividendOption: ProductApiTypes.DividendOption[req.dividendOption],
      dividendRatePercent: req.dividendRatePercent,
      dividendInterestAdjustment: req.dividendInterestAdjustment,
      startDate: req.asOfDate || req.startDate,
      taxRate: req.taxRate,
      loanRateType: ProductApiTypes.LoanRateType[req.loanRateType],
      insured: {
        gender: ProductApiTypes.Gender[req.gender === '1' ? 'M' : 'F'],
        dob: req.dob,
      },
      product: {
        type: ProductApiTypes.ProductType['Whole'],
        code: ProductApiTypes.ProductCode[req.productCode],
        state: ProductApiTypes.State[req.state],
      },
      segments: [], //generated below
      totalUnits: req.faceAmount,
      paymentFrequency: ProductApiTypes.PaymentFrequency[req.paymentFrequency],
      alirSchedules: [],
      tenThirtyFiveExchange: req.cashValueTransfer
        ? {
            premiumType: premiumType,
            cashValue: req.cashValueTransfer,
            costBasis: req.costBasis,
            startDate: Utils.offsetDate(req.startDate, req.expectedArrival, 'months'),
          }
        : null,
      asOfDate: req.asOfDate || req.startDate,
    };

    const rateClass = /\d+/.test(req.rateClass) ? req.rateClass : RateClasses[req.rateClass].code;
    const hasWaiver = req.waiverUnits > 0 ? true : false;
    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: req.asOfDate || req.startDate,
      rateClass: rateClass,
      amount: req.faceAmount,
      riders: req.riders,
      hasWaiver: hasWaiver,
      includeAdbr: true,
    };
    const segments = ProductServiceClient.generateProductServicePayloadSegments(segmentInfo);
    const alirSegment = remove(segments, (segment) => segment.riderType === RiderType.ALIR);
    this.massageAlirSegment(alirSegment);
    policy.segments = segments;
    policy.alirSchedules = alirSegment;
    const payload = {
      policy: policy,
    };
    return payload;
  }

  @serverOnly()
  private getConceptIllustrationProductServiceRequestPayload(
    req: IllustrationRequest,
    isSolve?: boolean
  ): ProductApiTypes.IllustrationPayloadReq {
    const policy = {
      version: null,
      dividendOption: ProductApiTypes.DividendOption[req.dividendOption],
      dividendRatePercent: req.dividendRatePercent,
      dividendInterestAdjustment: req.dividendInterestAdjustment,
      startDate: req.startDate,
      taxRate: req.taxRate,
      loanRateType: ProductApiTypes.LoanRateType[req.loanRateType],
      insured: {
        gender: ProductApiTypes.Gender[req.gender === '1' ? 'M' : 'F'],
        dob: req.dob,
      },
      product: {
        type: ProductApiTypes.ProductType['Whole'],
        code: ProductApiTypes.ProductCode[req.productCode],
        state: ProductApiTypes.State[req.state],
      },
      segments: [], //generated below
      totalUnits: req.faceAmount,
      paymentFrequency: ProductApiTypes.PaymentFrequency[req.paymentFrequency],
      alirSchedules: [],
    };

    const rateClass = /\d+/.test(req.rateClass) ? req.rateClass : RateClasses[req.rateClass].code;
    const hasWaiver = req.waiverUnits > 0 ? true : false;
    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: req.asOfDate || req.startDate,
      rateClass: rateClass,
      amount: req.faceAmount,
      riders: req.riders,
      hasWaiver: hasWaiver,
      includeAdbr: true,
    };
    const segments = ProductServiceClient.generateProductServicePayloadSegments(segmentInfo);
    const alirSegment = remove(segments, (segment) => segment.riderType === RiderType.ALIR);
    this.massageAlirSegment(alirSegment);
    policy.segments = segments;
    policy.alirSchedules = alirSegment;
    const payload: ProductApiTypes.IllustrationPayloadReq = {
      policy: policy,
    };

    if (isSolve) {
      if (!req.disbursementAmount) {
        payload.solver = {
          problemType: ProductApiTypes.IllustrationSolverProblemType.NotOverLoanByLoanAmount,
          roundToUnit: 1,
        };
      }

      payload.policy.distribution = {
        amount: req.disbursementAmount || 0,
        startAge: req.startAge || 0,
        endAge: req.endAge || 0,
        type:
          ProductApiTypes.DividendOption[req.dividendOption] === 'PUA'
            ? ProductApiTypes.DistributionType.WithdrawAndLoan
            : ProductApiTypes.DistributionType.Loan,
      };
    }

    return payload;
  }

  private massageAlirSegment(alirSegment) {
    const alirProps = ['startDate', 'alirPremium', 'plannedYears', 'paymentFrequency'];
    alirSegment.forEach((segment) => {
      Object.keys(segment).forEach((prop) => {
        if (segment.hasOwnProperty(prop) && !alirProps.includes(prop)) {
          delete segment[prop];
        }
      });
    });
  }

  @serverOnly()
  private getPremiumSolvePayload(
    options: PremiumSolveRequest,
    desiredPremium: number,
    version: string
  ): ProductApiTypes.PremiumSolvePayloadReq {
    const dob: Date = options.dob;
    const effectiveDate: Date = options.effectiveDate;
    const duration: number = options.term;
    const gender: string = options.gender === '1' ? 'M' : 'F';
    const product: string = options.product;
    const productType: string = options.productType;
    const state: string = options.state;
    const hasWaiver: boolean = options.waiver ? true : false;
    let rateClass: string = options.ratingCode.toString();
    // workaround for LifeBridge, which always expects a rateClass of ‘2’
    if (product === Product.products.ET10.code) {
      rateClass = '2';
    }
    // const paymentFrequency: string = ProductServiceClient.normalizePaymentFrequency(options.paymentFrequency);
    const flatExtras = options.flatExtras.map((fe) => {
      return { name: fe.ruleCode, rate: fe.amount };
    });
    const units: number = options.face;

    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: effectiveDate,
      rateClass: rateClass,
      amount: units,
      riders: options.riders,
      hasWaiver: hasWaiver,
      flatExtras: flatExtras,
      includeAdbr: false,
    };
    const segments = ProductServiceClient.generateProductServicePayloadSegments(segmentInfo, true);

    const payload: ProductApiTypes.PremiumSolvePayloadReq = {
      policies: [
        {
          version: version,
          asOfDate: options.asOfDate || effectiveDate,
          startDate: effectiveDate,
          duration: duration,
          totalUnits: units,
          premium: desiredPremium,
          paymentFrequency: ProductApiTypes.PaymentFrequency[options.paymentFrequency],
          product: {
            type: ProductApiTypes.ProductType[options.productType],
            code: ProductApiTypes.ProductCode[product],
            state: ProductApiTypes.State[state],
          },
          insured: {
            dob: dob,
            gender: ProductApiTypes.Gender[gender],
          },
          segments: segments,
        },
      ],
    };
    return payload;
  }
  // ====================== Queries =====================
  private getVSTQueryParameters() {
    return {
      payload: {
        policy: {
          illustrationList: [
            {
              policyYear: null,
              ageEndYear: null,
              totalDeathBenefitEndYear: null,
              totalCashValueEndYear: null,
              netCashValueEndYear: null,
              netDeathBenefitEndYear: null,
              guaranteedDeathBenefitEndYear: null,
              guaranteedCashValueEndYear: null,
              contractPremiumBegYear: null,
              guaranteedContractPremiumBegYear: null,
            },
          ],
          mecYear: null,
        },
      },
    };
  }

  //todo:HL-36257 - clean up/combine illustration calls after concept illustration
  private getIllustrationTabVSTQueryParameters(isSolve: boolean) {
    if (isSolve) {
      return {
        payload: {
          solver: {
            solvedValue: null,
          },
          policy: {
            illustrationList: [
              {
                policyYear: null,
                ageEndYear: null,
                totalDeathBenefitEndYear: null,
                totalCashValueEndYear: null,
                netCashValueEndYear: null,
                netDeathBenefitEndYear: null,
                guaranteedDeathBenefitEndYear: null,
                guaranteedCashValueEndYear: null,
                contractPremiumBegYear: null,
                guaranteedContractPremiumBegYear: null,
                annualDividendEndYear: null,
                paidUpAdditionsEndYear: null,
                totalOutstandingLoanEndYear: null,
                annualNetOutlayBegYear: null,
                withdrawAmount: null,
                annualLoanRequest: null,
                uncapitalizedLoanInterest: null,
              },
            ],
            mecYear: null,
          },
        },
      };
    }

    return {
      payload: {
        policy: {
          illustrationList: [
            {
              policyYear: null,
              ageEndYear: null,
              totalDeathBenefitEndYear: null,
              totalCashValueEndYear: null,
              netCashValueEndYear: null,
              netDeathBenefitEndYear: null,
              guaranteedDeathBenefitEndYear: null,
              guaranteedCashValueEndYear: null,
              contractPremiumBegYear: null,
              guaranteedContractPremiumBegYear: null,
              annualDividendEndYear: null,
              paidUpAdditionsEndYear: null,
              totalOutstandingLoanEndYear: null,
            },
          ],
          mecYear: null,
        },
      },
    };
  }

  private getPremiumSolveQueryParameters() {
    return {
      payload: {
        policies: [
          {
            version: null,
            startDate: null,
            asOfDate: null,
            duration: null,
            totalUnits: null,
            paymentFrequency: null,
            premium: null,
          },
        ],
      },
    };
  }
}
